import { Client, type Room } from '@colyseus/sdk';
import {
  SOCIAL_ROOM_NAME,
  type DirectMessage,
  type PresenceStatus,
  type SocialCommand,
  type SocialInvite,
  type SocialState,
} from '@moba/shared';
import { defaultEndpoint } from './NetClient.ts';

import { t, tr } from './i18n.ts';
/**
 * Conexión a la sala `social` mientras hay sesión: presencia, amigos,
 * invitaciones y mensajes privados. Se reconecta sola (con espera creciente)
 * si se corta, y vuelve a enviar el estado actual al reconectar.
 */
export interface SocialEvents {
  onState?: (state: SocialState) => void;
  onInvite?: (invite: SocialInvite) => void;
  onMessage?: (message: DirectMessage) => void;
  onError?: (message: string) => void;
  onNotice?: (message: string) => void;
  onConnection?: (connected: boolean) => void;
  /** el servidor no aceptó la sesión (caducó o se cerró en otro sitio) */
  onAuthLost?: () => void;
}

export class SocialClient {
  private readonly client = new Client(defaultEndpoint());
  private room?: Room;
  private token = '';
  private stopped = true;
  private retryMs = 2000;
  private retryTimer = 0;
  private status: PresenceStatus = { state: 'menu' };
  state?: SocialState;

  constructor(private readonly events: SocialEvents) {}

  get connected(): boolean {
    return Boolean(this.room);
  }

  connect(token: string): void {
    if (!this.stopped && this.token === token) return;
    this.disconnect();
    this.token = token;
    this.stopped = false;
    this.retryMs = 2000;
    void this.open();
  }

  disconnect(): void {
    this.stopped = true;
    window.clearTimeout(this.retryTimer);
    const room = this.room;
    this.room = undefined;
    this.state = undefined;
    if (room) void room.leave(true).catch(() => {});
  }

  /** Estado propio (menú, sala, partida…); sólo se envía si cambia. */
  setStatus(status: PresenceStatus): void {
    if (JSON.stringify(status) === JSON.stringify(this.status)) return;
    this.status = status;
    this.room?.send('social', { type: 'status', status } satisfies SocialCommand);
  }

  send(command: SocialCommand): void {
    if (!this.room) {
      this.events.onError?.(t('Sin conexión con el servidor de amigos'));
      return;
    }
    this.room.send('social', command);
  }

  private async open(): Promise<void> {
    if (this.stopped) return;
    let room: Room;
    try {
      room = await this.client.joinOrCreate(SOCIAL_ROOM_NAME, { token: this.token });
    } catch (error) {
      const code = (error as { code?: number }).code;
      const message = error instanceof Error ? error.message : String(error);
      if (code === 401 || /sesi[oó]n/i.test(message)) {
        this.stopped = true;
        this.events.onAuthLost?.();
        return;
      }
      this.scheduleRetry();
      return;
    }
    if (this.stopped) {
      void room.leave(true).catch(() => {});
      return;
    }
    this.room = room;
    this.retryMs = 2000;
    room.onMessage('social', (state: SocialState) => {
      this.state = state;
      this.events.onState?.(state);
    });
    room.onMessage('invite', (invite: SocialInvite) => this.events.onInvite?.(invite));
    room.onMessage('dm', (message: DirectMessage) => this.events.onMessage?.(message));
    room.onMessage('socialError', (message: { message: string }) => this.events.onError?.(tr(message.message)));
    room.onMessage('notice', (message: { message: string }) => this.events.onNotice?.(tr(message.message)));
    room.onMessage('pong', () => {});
    room.onLeave(() => {
      if (this.room !== room) return;
      this.room = undefined;
      this.events.onConnection?.(false);
      this.scheduleRetry();
    });
    this.events.onConnection?.(true);
    // el servidor arranca cada conexión en "menú": se le recuerda el estado real
    room.send('social', { type: 'status', status: this.status } satisfies SocialCommand);
  }

  private scheduleRetry(): void {
    if (this.stopped) return;
    window.clearTimeout(this.retryTimer);
    this.retryTimer = window.setTimeout(() => void this.open(), this.retryMs);
    this.retryMs = Math.min(30000, this.retryMs * 2);
  }
}
